/* eslint-disable react-hooks/exhaustive-deps */
// 取込ファイル明細ビューア（内部調査用 / localのみリンク表示）
// 1つの取込ファイル(=1日報)から生まれた「生データ(ESTRA配送明細)」と
// 「集計デジタコ勤怠」を左右のグリッドで突合表示する。
import * as wjCore from "@grapecity/wijmo"
import * as wjcGrid from "@grapecity/wijmo.grid"
import * as wjrGrid from "@grapecity/wijmo.react.grid"

import dayjs from "dayjs"
import queryString from "query-string"
import { useEffect, useRef, useState } from "react"
import { Button, Card, Col, Container, Row } from "react-bootstrap"
import { useHistory, useLocation } from "react-router-dom"
import FlexGridOuter from "../../../components/FlexGridWrapper/FlexGridOuter"
import Spinner from "../../../components/Spinner"
import CONST from "../../../constant"
import {
  TDezitakoFileDetailRes,
  TDezitakoFileKintai,
  TDezitakoFileMeisai,
} from "../../../service/truckcontrol/dezitako/TDegitako"
import DezitakoService from "../../../service/truckcontrol/dezitako/TDezitakoService"
import dateUtil from "../../../ts/util/dateUtil"

/**
 * 始業/終業の「日付 時刻」表示文字列を作る
 * @param date yyyy/mm/dd 形式の日付
 * @param ms ミリ秒（TimeSpan由来）
 */
const formatDateTime = (date?: string, ms?: number): string => {
  if (date == null && ms == null) return ""
  const d = date == null ? "" : dateUtil.format(date, "MM/DD")
  const t = ms == null ? "" : dateUtil.toTimeSpanString(ms, "HH:mm")
  return `${d} ${t}`.trim()
}

/**
 * 時間(ミリ秒)を HH:mm 文字列にする
 * 突合調査で「値なし」と「0分」を区別できるよう、nullは空欄にする
 * （dateUtil.toTimeSpanString は null を "00:00" に整形してしまう）
 * @param ms ミリ秒（TimeSpan由来）
 */
const formatTimeSpan = (ms?: number): string =>
  ms == null ? "" : dateUtil.toTimeSpanString(ms, "HH:mm")

/**
 * 生データ（ESTRA配送明細）の日付・時刻文字列をエポックmsにする
 * バックエンドの集計（DateUtils.ToDateWithDelimiter / ToTimeSpanSimplyWithoutSeconds）に
 * 合わせ、2桁年は現在世紀に補正し、秒は切り捨てる
 * @param date "yyyy/mm/dd" または "yy/mm/dd"
 * @param time "HH:mm" または "HH:mm:ss"
 */
const parseRawDateTime = (date?: string, time?: string): number | undefined => {
  if (!date) return undefined
  const [y, m, d] = date.split("/").map(Number)
  if ([y, m, d].some((v) => v == null || Number.isNaN(v))) return undefined
  const year =
    y < 100 ? y + Math.floor(new Date().getFullYear() / 100) * 100 : y
  const [hh, mm] = (time ?? "").split(":").map(Number)
  return new Date(
    year,
    m - 1,
    d,
    Number.isNaN(hh) ? 0 : (hh ?? 0),
    Number.isNaN(mm) ? 0 : (mm ?? 0),
  ).getTime()
}

/**
 * 集計勤怠の日付(DateTime文字列)＋時刻(ミリ秒)をエポックmsにする
 * 時刻は型定義上 number だが、バックエンドの TimeSpanConverter が
 * TotalMilliseconds を文字列で返すため、実際は "30600000" のような文字列で届く。
 * そのまま加算すると文字列連結になるので必ず Number() で数値化する
 */
const toKintaiTs = (
  date?: string,
  ms?: number | string,
): number | undefined => {
  if (date == null || ms == null) return undefined
  const msNumber = Number(ms)
  if (Number.isNaN(msNumber)) return undefined
  return dayjs(date).startOf("day").valueOf() + msNumber
}

/** グリッド間の対応行ハイライト用クラス（_flexgrid.scss で定義） */
const LINKED_ROW_CLASS = "row-linked"

/** null許容の数値比較（nullは末尾扱い）。ソートの複合キー用 */
const compareNullable = (a?: number, b?: number): number => {
  const x = a ?? Infinity
  const y = b ?? Infinity
  return x === y ? 0 : x < y ? -1 : 1
}

const DezitakoFileDetail = () => {
  const history = useHistory()
  const location = useLocation()

  const [isSearching, setIsSearching] = useState(false)
  const [detail, setDetail] = useState<TDezitakoFileDetailRes>()
  const [loadError, setLoadError] = useState<string>()

  // リストア制御用フラグ（本画面ではリストアしないが、FlexGridOuterの必須propのため用意）
  const [isRestoreSearching, setIsRestoreSearching] = useState<boolean>(false)

  // 生データ（ESTRA配送明細）グリッド
  const [meisaiFlexGrid, setMeisaiFlexGrid] = useState<wjcGrid.FlexGrid>()
  const [meisaiGridData, setMeisaiGridData] = useState<{
    data: wjCore.CollectionView
  }>({ data: new wjCore.CollectionView([]) })
  const [meisaiGridInitialized, setMeisaiGridInitialized] =
    useState<boolean>(false)
  const [meisaiPageSize, setMeisaiPageSize] = useState<number>(0)

  // 集計デジタコ勤怠グリッド
  const [kintaiFlexGrid, setKintaiFlexGrid] = useState<wjcGrid.FlexGrid>()
  const [kintaiGridData, setKintaiGridData] = useState<{
    data: wjCore.CollectionView
  }>({ data: new wjCore.CollectionView([]) })
  const [kintaiGridInitialized, setKintaiGridInitialized] =
    useState<boolean>(false)
  const [kintaiPageSize, setKintaiPageSize] = useState<number>(0)

  // イベントハンドラはグリッド初期化時に一度だけ登録されるため、
  // state経由だと古いクロージャを掴む。相手グリッドの参照はrefで持つ
  const meisaiGridRef = useRef<wjcGrid.FlexGrid>()
  const kintaiGridRef = useRef<wjcGrid.FlexGrid>()

  //#region [対応行ハイライト]
  // 生データ行の着〜発と集計勤怠行の始業〜終業の「時間帯の重なり」で対応を判定する。
  // 選択そのものを相互に同期すると無限ループになるため、相手グリッドは
  // row-linked クラスの付け外し（色変化）のみとし、発火もユーザー操作
  // （クリック / ↑↓キー）に限定する（selectionChanged にはフックしない）。

  /** 指定グリッドの各行に対し、条件を満たす行だけ row-linked クラスを付ける */
  const setRowHighlight = (
    grid: wjcGrid.FlexGrid | undefined,
    predicate: (item: any) => boolean,
  ) => {
    if (!grid) return
    grid.rows.forEach((row) => {
      const classes = (row.cssClassAll?.split(" ") ?? []).filter(
        (c) => c !== "" && c !== LINKED_ROW_CLASS,
      )
      if (row.dataItem != null && predicate(row.dataItem)) {
        classes.push(LINKED_ROW_CLASS)
      }
      row.cssClassAll = classes.join(" ")
    })
    // 相手グリッド側には選択変更などの再描画契機がないため明示的に再描画する
    grid.invalidate()
  }

  /** 時間帯 [aStart, aEnd] と [bStart, bEnd] が重なるか（境界一致も重なり扱い） */
  const isOverlap = (
    aStart?: number,
    aEnd?: number,
    bStart?: number,
    bEnd?: number,
  ): boolean => {
    if (aStart == null || aEnd == null || bStart == null || bEnd == null)
      return false
    return aStart <= bEnd && bStart <= aEnd
  }

  /** 生データ側の選択行に対応する集計勤怠行をハイライトする */
  const highlightKintaiFromMeisai = () => {
    const meisaiGrid = meisaiGridRef.current
    // 操作した側に相手側の操作で付いた前回ハイライトが残ると紛らわしいため、まず外す
    setRowHighlight(meisaiGrid, () => false)
    const item = meisaiGrid?.rows[meisaiGrid.selection?.row]?.dataItem
    if (item == null) return
    // 着のみ・発のみの行（点呼など）は片側の値で1点として扱う
    const start = item.着ts ?? item.発ts
    const end = item.発ts ?? item.着ts
    setRowHighlight(kintaiGridRef.current, (k) =>
      isOverlap(start, end, k.始業ts, k.終業ts),
    )
  }

  /** 集計勤怠側の選択行に対応する生データ行をすべてハイライトする */
  const highlightMeisaiFromKintai = () => {
    const kintaiGrid = kintaiGridRef.current
    // 操作した側に相手側の操作で付いた前回ハイライトが残ると紛らわしいため、まず外す
    setRowHighlight(kintaiGrid, () => false)
    const item = kintaiGrid?.rows[kintaiGrid.selection?.row]?.dataItem
    if (item == null) return
    setRowHighlight(meisaiGridRef.current, (m) =>
      isOverlap(m.着ts ?? m.発ts, m.発ts ?? m.着ts, item.始業ts, item.終業ts),
    )
  }

  /**
   * ユーザー操作（クリック / ↑↓キー）のときだけハイライトを発火させるリスナーを登録する
   * click は mousedown での選択更新後、keyup は keydown での選択移動後に発火するため、
   * ハンドラ内で grid.selection を読めば移動後の行が取れる
   */
  const addHighlightListeners = (
    grid: wjcGrid.FlexGrid,
    highlight: () => void,
  ) => {
    grid.hostElement.addEventListener("click", () => highlight())
    grid.hostElement.addEventListener("keyup", (e) => {
      if (e.key === "ArrowUp" || e.key === "ArrowDown") highlight()
    })
  }

  const meisaiGridInitializedHandler = (grid: wjcGrid.FlexGrid) => {
    meisaiGridRef.current = grid
    addHighlightListeners(grid, highlightKintaiFromMeisai)
  }

  const kintaiGridInitializedHandler = (grid: wjcGrid.FlexGrid) => {
    kintaiGridRef.current = grid
    addHighlightListeners(grid, highlightMeisaiFromKintai)
  }
  //#endregion

  useEffect(() => {
    document.title = `取込ファイル明細（調査用）`
    const query = queryString.parse(location.search)
    const torikomiFileId = Number(query.torikomiFileId)
    if (!torikomiFileId || Number.isNaN(torikomiFileId)) {
      return
    }
    load(torikomiFileId)
  }, [])

  const load = async (torikomiFileId: number) => {
    setIsSearching(true)
    setLoadError(undefined)
    try {
      const res = await new DezitakoService().getFileDetail(torikomiFileId)
      const data = res.data
      setDetail(data)
      setMeisaiGridData({
        data: new wjCore.CollectionView(
          createMeisaiRows(data.生明細List ?? []),
        ),
      })
      setKintaiGridData({
        data: new wjCore.CollectionView(
          createKintaiRows(data.集計勤怠List ?? []),
        ),
      })
    } catch (err) {
      // 調査用ツールのため、画面に簡易表示
      setLoadError(
        `取込ファイル明細の取得に失敗しました。（取込ファイルID: ${torikomiFileId}）`,
      )
      // eslint-disable-next-line no-console
      console.error(err)
    } finally {
      setIsSearching(false)
    }
  }

  /** 生データ表示用行を作成（時系列突合のため明細行番号の昇順に固定） */
  const createMeisaiRows = (list: TDezitakoFileMeisai[]) =>
    list
      .map((e) => ({
        行: e.明細行番号,
        着地作業: e.着地作業,
        着: `${e.着日付 ?? ""} ${e.着時刻 ?? ""}`.trim(),
        発: `${e.発日付 ?? ""} ${e.発時刻 ?? ""}`.trim(),
        作業時間: e.作業時間,
        集配先: e.集配先,
        // 以下は非表示項目。集計勤怠グリッドとの対応行ハイライト判定に使う
        着ts: parseRawDateTime(e.着日付, e.着時刻),
        発ts: parseRawDateTime(e.発日付, e.発時刻),
      }))
      .sort((a, b) => (Number(a.行) || 0) - (Number(b.行) || 0))

  /** 集計勤怠表示用行を作成（時系列突合のため勤務日→始業日時→終業日時の昇順に固定） */
  const createKintaiRows = (list: TDezitakoFileKintai[]) =>
    list
      .map((e) => ({
        勤務日: e.勤務日 == null ? "" : dateUtil.format(e.勤務日, "YYYY/MM/DD"),
        始業: formatDateTime(e.始業日付, e.始業時刻),
        終業: formatDateTime(e.終業日付, e.終業時刻),
        勤務: formatTimeSpan(e.勤務時間),
        残業: formatTimeSpan(e.残業時間),
        深夜: formatTimeSpan(e.深夜時間),
        休憩: formatTimeSpan(e.休憩時間),
        休息: formatTimeSpan(e.休息時間),
        荷積: formatTimeSpan(e.荷積時間),
        荷卸: formatTimeSpan(e.荷卸時間),
        待機: formatTimeSpan(e.待機時間),
        // 以下は非表示項目。生データグリッドとの対応行ハイライト判定に使う
        始業ts: toKintaiTs(e.始業日付, e.始業時刻),
        終業ts: toKintaiTs(e.終業日付, e.終業時刻),
      }))
      // 勤務日→始業日時→終業日時の複合昇順。値なしの行は各キーで末尾扱い
      .sort(
        (a, b) =>
          (a.勤務日 || "9999/99/99").localeCompare(b.勤務日 || "9999/99/99") ||
          compareNullable(a.始業ts, b.始業ts) ||
          compareNullable(a.終業ts, b.終業ts)
      )

  return (
    <Container fluid>
      <Row className="page-title-row">
        <Col className="page-title-col">
          <span className="page-title">デジタコ取込データ確認</span>
        </Col>
      </Row>
      {/* Spinnerはposition:absolute/height:100%のオーバーレイのため、
          positionedな親で包まないとビューポート基準になりページ下に不可視のはみ出しを作る */}
      <div className="position-relative">
        <Spinner searching={isSearching}></Spinner>
        <Row>
          <Col>
            <Card className="mb-2">
              <Card.Body>
                {/* 基本情報。日報番号・乗務員・車番は1ファイル=1日報なので生明細の先頭行から表示する */}
                <div className="d-flex flex-wrap gap-3">
                  <div>
                    <strong>ファイル名：</strong>
                    {detail?.ファイル名 ?? "-"}
                  </div>
                  <div>
                    <strong>取込ファイルID：</strong>
                    {detail?.取込ファイルID ?? "-"}
                  </div>
                  <div>
                    <strong>取込データ年月：</strong>
                    {detail?.取込データ年月 == null
                      ? "-"
                      : dateUtil.format(detail.取込データ年月, "YYYY/MM")}
                  </div>
                  <div>
                    <strong>日報番号：</strong>
                    {detail?.生明細List?.[0]?.日報番号 ?? "-"}
                  </div>
                  <div>
                    <strong>乗務員：</strong>
                    {detail?.生明細List?.[0] == null
                      ? "-"
                      : `${detail.生明細List[0].乗務員コード ?? ""} ${
                          detail.生明細List[0].乗務員 ?? ""
                        }`.trim() || "-"}
                  </div>
                  <div>
                    <strong>車番：</strong>
                    {detail?.生明細List?.[0]?.車番 ?? "-"}
                  </div>
                </div>
                {loadError && (
                  <div className="text-danger" role="alert">
                    {loadError}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Row>
          {/* 左：生データ（ESTRA配送明細） */}
          <Col xs={6}>
            <Card>
              <Card.Header>生データ（ESTRA配送明細）</Card.Header>
              <Card.Body>
                <FlexGridOuter
                  gridId="DezitakoFileDetail_Meisai"
                  flexGrid={meisaiFlexGrid}
                  setFlexGrid={setMeisaiFlexGrid}
                  gridConstantProperties={
                    CONST.GRID.PROPERTY_SET.FLEX_GRID_PROPS
                  }
                  gridData={meisaiGridData}
                  setGridData={setMeisaiGridData}
                  gridInitialized={meisaiGridInitialized}
                  setGridInitialized={setMeisaiGridInitialized}
                  isRestoreSearching={isRestoreSearching}
                  setIsRestoreSearching={setIsRestoreSearching}
                  pageNumber={0}
                  pageSize={meisaiPageSize}
                  setPageSize={setMeisaiPageSize}
                  gridInitializedHandler={meisaiGridInitializedHandler}
                  isEnablePageSize="hidden"
                  excelFileName="取込ファイル明細_生データ.xlsx"
                  pdfFileName="取込ファイル明細_生データ.pdf"
                  gridProperties={{
                    // 時系列突合の画面のためソート禁止（明細行番号の昇順に固定）
                    allowSorting: wjcGrid.AllowSorting.None,
                  }}>
                  <wjrGrid.FlexGridColumn binding="行" header="行" width={50} />
                  <wjrGrid.FlexGridColumn
                    binding="着地作業"
                    header="着地作業"
                    width={90}
                  />
                  <wjrGrid.FlexGridColumn
                    binding="着"
                    header="着"
                    width={150}
                  />
                  <wjrGrid.FlexGridColumn
                    binding="発"
                    header="発"
                    width={150}
                  />
                  <wjrGrid.FlexGridColumn
                    binding="作業時間"
                    header="作業時間"
                    width={90}
                  />
                  <wjrGrid.FlexGridColumn
                    binding="集配先"
                    header="集配先"
                    width="*"
                    minWidth={150}
                  />
                </FlexGridOuter>
              </Card.Body>
            </Card>
          </Col>
          {/* 右：集計後デジタコ勤怠 */}
          <Col xs={6}>
            <Card>
              <Card.Header>集計デジタコ勤怠（同一ファイル由来）</Card.Header>
              <Card.Body>
                <FlexGridOuter
                  gridId="DezitakoFileDetail_Kintai"
                  flexGrid={kintaiFlexGrid}
                  setFlexGrid={setKintaiFlexGrid}
                  gridConstantProperties={
                    CONST.GRID.PROPERTY_SET.FLEX_GRID_PROPS
                  }
                  gridData={kintaiGridData}
                  setGridData={setKintaiGridData}
                  gridInitialized={kintaiGridInitialized}
                  setGridInitialized={setKintaiGridInitialized}
                  isRestoreSearching={isRestoreSearching}
                  setIsRestoreSearching={setIsRestoreSearching}
                  pageNumber={0}
                  pageSize={kintaiPageSize}
                  setPageSize={setKintaiPageSize}
                  gridInitializedHandler={kintaiGridInitializedHandler}
                  isEnablePageSize="hidden"
                  excelFileName="取込ファイル明細_集計勤怠.xlsx"
                  pdfFileName="取込ファイル明細_集計勤怠.pdf"
                  gridProperties={{
                    // 時系列突合の画面のためソート禁止（始業日時の昇順に固定）
                    allowSorting: wjcGrid.AllowSorting.None,
                  }}>
                  <wjrGrid.FlexGridColumn
                    binding="勤務日"
                    header="勤務日"
                    width={100}
                  />
                  <wjrGrid.FlexGridColumn
                    binding="始業"
                    header="始業"
                    width={110}
                  />
                  <wjrGrid.FlexGridColumn
                    binding="終業"
                    header="終業"
                    width={110}
                  />
                  <wjrGrid.FlexGridColumn
                    binding="勤務"
                    header="勤務"
                    width={60}
                  />
                  <wjrGrid.FlexGridColumn
                    binding="残業"
                    header="残業"
                    width={60}
                  />
                  <wjrGrid.FlexGridColumn
                    binding="深夜"
                    header="深夜"
                    width={60}
                  />
                  <wjrGrid.FlexGridColumn
                    binding="休憩"
                    header="休憩"
                    width={60}
                  />
                  <wjrGrid.FlexGridColumn
                    binding="休息"
                    header="休息"
                    width={60}
                  />
                  <wjrGrid.FlexGridColumn
                    binding="荷積"
                    header="荷積"
                    width={60}
                  />
                  <wjrGrid.FlexGridColumn
                    binding="荷卸"
                    header="荷卸"
                    width={60}
                  />
                  <wjrGrid.FlexGridColumn
                    binding="待機"
                    header="待機"
                    width={60}
                  />
                </FlexGridOuter>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Row className="mt-2 mb-3">
          <Col>
            {/* 一覧へは戻る遷移とし、デジタコ勤怠確認側の検索条件(クエリ)を維持する */}
            <Button
              type="button"
              variant="secondary"
              disabled={history.length <= 1}
              onClick={() => history.goBack()}>
              一覧に戻る
            </Button>
          </Col>
        </Row>
      </div>
    </Container>
  )
}

export default DezitakoFileDetail
